<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, toRefs } from "vue";
import Button from "@/stories/buttons/Button/Button.vue";
import TextInput from "@/stories/form/TextInput/TextInput.vue";
import { BREAKPOINTS, MAX_PIXABAY_PAGES, PIXABAY_API_KEY } from "@/consts";
import type { IPixabayImage, IPixabayResult } from "@/interfaces";
import { sentryException } from "@/sentry";
import { simpleError } from "@/stories/displays/SimpleError/SimpleError.vue";
import ImageCheckbox from "@/stories/form/ImageCheckbox/ImageCheckbox.vue";
import { useBreakpoints } from "@vueuse/core";
import { debounce } from "lodash";
import { useAuthStore } from "@/stores/auth.module";
import BigDialog from "@/stories/dialogs/BigDialog.vue";
import DialogHeader from "@/stories/dialogs/DialogHeader.vue";
import DialogSubHeader from "@/stories/dialogs/DialogSubHeader.vue";
import DialogContent from "@/stories/dialogs/DialogContent.vue";
import SimpleLoader from "@/stories/displays/SimpleLoader/SimpleLoader.vue";

interface Props {
  max: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  select: [IPixabayImage[]];
}>();

const authStore = useAuthStore();
const breakpoints = useBreakpoints(BREAKPOINTS);
const isMobile = breakpoints.smaller("desktop");

const { max } = toRefs(props);
const page = ref(1);
const selectedImages = ref<number[]>([]);
const loading = ref<string | null>(null);
const searchText = ref("");
const searchResult = ref<IPixabayImage[]>([]);
const loadMoreTrigger = ref<HTMLElement | null>(null);

let observer: IntersectionObserver | null = null;

const loadNextPage = async () => {
  if (loading.value) {
    return;
  }

  if (page.value >= MAX_PIXABAY_PAGES) {
    loading.value = null;
    return;
  }

  page.value++;
  await fetchImages();
};

onMounted(() => {
  observer = new IntersectionObserver(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        loadNextPage();
      }
    },
  );

  if (loadMoreTrigger.value) {
    observer.observe(loadMoreTrigger.value);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
});

const images = computed(() => {
  if (!searchResult.value) {
    return [];
  }

  return [...searchResult.value];
});

const fetchImages = async (event?: Event) => {
  event?.preventDefault();

  if (searchText.value.trim().length === 0) {
    return;
  }

  try {
    loading.value = "templates.pixabay.loading_message";

    const url = new URL("https://pixabay.com/api/");
    url.searchParams.append("key", PIXABAY_API_KEY);
    url.searchParams.append("q", searchText.value);
    url.searchParams.append("lang", authStore.institutionLocale);
    url.searchParams.append("image_type", "photo");
    url.searchParams.append("min_width", "200");
    url.searchParams.append("min_height", "200");
    url.searchParams.append("page", page.value.toString());
    url.searchParams.append("per_page", "16");
    const response = await fetch(url);
    const result: IPixabayResult = await response.json();
    searchResult.value.push(...result.hits);
  } catch (error) {
    sentryException(error);
    simpleError("global.error_api");
  } finally {
    loading.value = null;
  }
};

const toggleImages = (image: IPixabayImage) => {
  if (selectedImages.value.includes(image.id)) {
    selectedImages.value = selectedImages.value.filter((id) => image.id !== id);
  } else {
    selectedImages.value.push(image.id);
  }
};

const handleApply = () => {
  const selections = (searchResult.value || []).filter((item) =>
    selectedImages.value.includes(item.id),
  );
  emit("select", selections);
};

const updateSearchText = async () => {
  searchResult.value = [];
  page.value = 1;
  selectedImages.value = [];
  await fetchImages();
};

const debouncedUpdateSearchText = debounce(updateSearchText, 300);

const handleSearchUpdate = (value: string) => {
  searchText.value = value;
  debouncedUpdateSearchText();
};

const isSelecteableMore = computed(() => {
  return selectedImages.value.length < max.value;
});
</script>

<template>
  <BigDialog @close="$emit('close')">
    <DialogHeader @close="$emit('close')">
      {{ $t("templates.online_library") }}
    </DialogHeader>

    <DialogSubHeader>
      <form
        @submit="fetchImages"
        class="flex justify-between items-center gap-4 w-full"
      >
        <TextInput
          name="search"
          rounded
          size="sm"
          icon="fa fa-search"
          background="bg-neutral100"
          :placeholder="$t('templates.search_image')"
          :helpText="
            !searchText.length && !searchResult
              ? $t('templates.pixabay.search_help_text')
              : undefined
          "
          autoFocus
          :modelValue="searchText"
          @update:modelValue="handleSearchUpdate"
        />

        <Button
          iconOnly
          icon="fa-search w-10 min-w-10"
          type="white"
          :buttonType="'submit'"
          @click="fetchImages"
        />
      </form>
    </DialogSubHeader>

    <DialogContent class="pixabay-dialog-content">
      <div class="flex flex-col gap-4 space-between relative">
        <div
          class="grid grid-cols-2 gap-4 desktop:mt-0 desktop:grid-cols-4 min-h-[27.125rem] max-h-[30rem]"
        >
          <ImageCheckbox
            v-for="image of images"
            lazyLoading
            lazyLoadingSelector="pixabay-dialog-content"
            :disabled="!isSelecteableMore"
            :key="image.id"
            :src="image.largeImageURL"
            :alt="image.tags"
            :checked="selectedImages.includes(image.id)"
            @toggle="() => toggleImages(image)"
          />
          <div ref="loadMoreTrigger" class="col-span-full h-1">
            <SimpleLoader v-if="loading" />
          </div>
        </div>
      </div>
    </DialogContent>

    <div
      :class="[
        'flex space-between items-center fixed left-0 bottom-0 bg-white w-full p-6 border-t mt-1 border-neutral300',
        'tablet:relative tablet:border-none',
      ]"
    >
      <div v-if="!isMobile" class="flex-grow h600 text-darkOcean600">
        {{
          $t("templates.amount_images_chosen_new", {
            count: selectedImages.length,
          })
        }}
      </div>
      <Button
        v-if="!isMobile"
        :text="$t('templates.cancel')"
        type="white"
        class="mr-2"
        @click="$emit('close')"
      />
      <Button
        type="primary"
        :text="$t('global.use')"
        :full-width="isMobile"
        :size="isMobile ? 'lg' : 'md'"
        @click="handleApply"
      />
    </div>
  </BigDialog>
</template>
